import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Seo, breadcrumbLd, faqLd, ORIGIN } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { countyIntro } from "@/lib/copy";
import { ArrowUpRight, Loader2 } from "lucide-react";

export default function CountyHub() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.geoCounty(slug).then(({ data }) => setData(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="h-[50vh] flex flex-col items-center justify-center gap-4"><p className="font-display text-3xl">County not found</p><Link to="/schools" className="bg-ink text-ivory px-6 py-3 font-label text-[10px] rounded-sm">All Schools</Link></div>;
  if (!data) return <div className="h-[50vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-rosemetal" /></div>;

  const crumbs = [{ name: "Home", path: "/" }, { name: "Schools", path: "/schools" }, { name: `${data.name} County`, path: `/schools/county/${slug}` }];
  const students = data.schools.reduce((a, s) => a + (s.students || 0), 0);
  const cities = [...new Set(data.schools.map((s) => s.city))];
  const faqs = [
    { q: `Where can students in ${data.name} County buy prom dresses?`, a: `Students across ${data.schools.length} ${data.name} County high schools shop Heather's Boutique for prom, homecoming and graduation formalwear, with a dress collection exclusive to each school.` },
    { q: `Which cities in ${data.name} County does Heather's Boutique serve?`, a: `We serve ${cities.slice(0, 8).join(", ")}${cities.length > 8 ? " and more" : ""} in ${data.name} County.` },
  ];

  return (
    <div data-testid="county-hub-page" className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-12">
      <Seo
        title={`Prom Dresses in ${data.name} County, WI`}
        description={`Prom, homecoming & graduation dresses for ${data.name} County, WI. Serving ${data.schools.length} high schools with dress locks.`}
        path={`/schools/county/${slug}`}
        jsonLd={[
          breadcrumbLd(crumbs),
          faqLd(faqs),
          { "@context": "https://schema.org", "@type": "ItemList", name: `High schools in ${data.name} County`, numberOfItems: data.schools.length, itemListElement: data.schools.map((s, i) => ({ "@type": "ListItem", position: i + 1, url: `${ORIGIN}/schools/${s.slug || s.id}`, name: s.name })) },
        ]}
      />
      <Breadcrumbs items={crumbs} />
      <p className="font-label text-[10px] text-rosemetal mb-2">{data.name} County · Wisconsin</p>
      <h1 className="font-display text-5xl sm:text-6xl mb-4">Prom Dresses in {data.name} County</h1>
      <p className="font-body text-base text-muted-foreground max-w-2xl mb-6 leading-relaxed">{countyIntro(data.name, data.schools.length, students)}</p>
      <div className="flex gap-3 mb-12">
        <Link to="/prom-dresses" className="bg-ink text-ivory px-6 py-3 font-label text-[10px] rounded-sm">Shop Prom</Link>
        <Link to="/schools" className="border border-ink px-6 py-3 font-label text-[10px] rounded-sm">All Schools</Link>
      </div>

      <h2 className="font-display text-3xl mb-6">{data.schools.length} schools in {data.name} County</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {data.schools.map((s) => (
          <Link key={s.id} to={`/schools/${s.slug || s.id}`} data-testid={`school-${s.id}`} className="group border border-border rounded-sm p-6 hover:border-rosemetal transition-colors">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-2xl group-hover:text-rosemetal transition-colors leading-tight">{s.name}</h3>
                <p className="font-body text-xs text-muted-foreground mt-1.5"><Link to={`/prom-dresses/city/${s.city_slug}`} className="hover:text-rosemetal underline-offset-2 hover:underline">{s.city}</Link> · {s.mascot || "—"}{s.students ? ` · ${s.students} students` : ""}</p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-rosemetal shrink-0" />
            </div>
            <div className="mt-4 h-1 rounded-full" style={{ background: s.primary_color }} />
          </Link>
        ))}
      </div>

      <section className="max-w-[900px] mt-20" data-testid="county-faq">
        <h2 className="font-display text-4xl mb-8">{data.name} County — FAQ</h2>
        <div className="divide-y divide-border border-y border-border">
          {faqs.map((f, i) => (
            <details key={i} className="group py-5">
              <summary className="font-display text-xl cursor-pointer list-none flex justify-between items-center">{f.q}<span className="text-rosemetal text-2xl group-open:rotate-45 transition-transform">+</span></summary>
              <p className="font-body text-sm text-muted-foreground mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
